"use client";

import { useCallback, useMemo } from "react";

import { useSessionQuery } from "@saas/account/data-access";
import {
  AccessesKeyEnum,
  accessRouteMapping,
  getProfileFromSession,
} from "@saas/account/utils";
import { subaccountRoutes } from "@saas/config/dashboard";
import { env } from "@saas/config/shared";

import { isNull } from "lodash-es";

// `view` keys match by equality, so child routes fail. These roots opt into
// prefix matching because their sections have detail pages.
const PREFIX_MATCH_VIEW_ROUTES = [
  "/order",
  "/consignment/customer-stocks",
  "/consignment/deliveries",
  "/consignment/invoices",
];

export type UsePermissionsReturnType = {
  hasPermission: (route: string, state?: "dashboard" | "pos") => boolean;
  hasParentId: string | null;
  accesses?: AccessesKeyEnum[];
  hasOrderActionPermission: boolean;
  hasMarketplaceActionPermission: boolean;
  hasAccessPermission: (key: AccessesKeyEnum) => boolean;
};

// Trailing path segments that mutate data. `view` access must not reach them
// even when the section root opts into prefix matching.
const ACTION_ONLY_ROUTE_SEGMENTS = ["manual-adjustment"];

const isActionOnlyRoute = (route: string) =>
  ACTION_ONLY_ROUTE_SEGMENTS.includes(route.split("/").pop() ?? "");

const isSubPath = (route: string, base: string) =>
  route === base || route.startsWith(`${base}/`);

export const usePermissions = (): UsePermissionsReturnType => {
  const { data: accesses } = useSessionQuery({
    select: (data) => {
      const profile = getProfileFromSession(data);

      return profile?.accesses ?? [];
    },
  });

  const { data: parentUserId } = useSessionQuery({
    select: (data) => {
      const profile = getProfileFromSession(data);

      return profile?.parentUserId ?? null;
    },
  });

  const accessRoutes = useMemo(() => {
    const accessRoute = accesses
      ?.map((access) => {
        const route = accessRouteMapping[access];

        if (!route) {
          return null;
        }

        return access.includes("all") || access.includes("action")
          ? { full: route }
          : { view: route };
      })
      .filter(
        (route): route is { full: string } | { view: string } => route !== null
      );

    if (!accessRoute?.length) {
      return [];
    }

    return [...new Set([{ full: subaccountRoutes[0] }, ...accessRoute])];
  }, [accesses]);

  const checkPosAllAccess = () => {
    return accesses ? accesses.includes(AccessesKeyEnum.POS_ALL) : false;
  };

  const checkOrderAccess = () => {
    return accesses?.includes(AccessesKeyEnum.POS_ALL)
      ? accesses.includes(AccessesKeyEnum.ORDER_ACTION) ||
          accesses.includes(AccessesKeyEnum.ORDER_VIEW)
      : false;
  };

  const checkPosAccess = useCallback((currentRoute: string) => {
    return currentRoute !== "/" ? checkOrderAccess() : checkPosAllAccess();
  }, []);

  const normalizeRoute = (route: string) => {
    const url = new URL(route, env.DASHBOARD_URL);
    return url.pathname;
  };

  const checkRouteAccess = useCallback(
    (currentRoute: string) => {
      const normalizedRoute = normalizeRoute(currentRoute);

      const requiresActionAccess = isActionOnlyRoute(normalizedRoute);

      return accessRoutes.some((routeObj) => {
        const [key, value] = Object.entries(routeObj)[0];
        const normalizedValue = normalizeRoute(value);

        if (requiresActionAccess && key !== "full") {
          return false;
        }

        if (normalizedValue !== "/" && normalizedRoute !== "/") {
          const isPrefixMatch =
            key === "full" ||
            PREFIX_MATCH_VIEW_ROUTES.includes(normalizedValue);

          return isPrefixMatch
            ? isSubPath(normalizedRoute, normalizedValue)
            : isSubPath(normalizedValue, normalizedRoute);
        }

        return normalizedRoute === "/" && normalizedValue === "/";
      });
    },
    [accessRoutes]
  );

  const hasPermission = useCallback(
    (currentRoute: string, state: "dashboard" | "pos" = "dashboard") =>
      state === "pos"
        ? checkPosAccess(currentRoute)
        : checkRouteAccess(currentRoute),

    [checkPosAccess, checkRouteAccess]
  );

  const hasParentId = useMemo(() => {
    if (parentUserId) {
      return parentUserId;
    }

    return null;
  }, [parentUserId]);

  const hasAccessPermission = useCallback(
    (key: AccessesKeyEnum) => isNull(hasParentId) || !!accesses?.includes(key),
    [accesses, hasParentId]
  );

  const hasOrderActionPermission = hasAccessPermission(
    AccessesKeyEnum.ORDER_ACTION
  );

  const hasMarketplaceActionPermission = hasAccessPermission(
    AccessesKeyEnum.MARKETPLACE_CONNECTION_ACTION
  );

  return {
    hasParentId,
    hasPermission,
    accesses,
    hasOrderActionPermission,
    hasMarketplaceActionPermission,
    hasAccessPermission,
  };
};

export default usePermissions;
